import classNames from 'classnames';
import PropTypes from 'prop-types';

import { QUESTION_TYPES } from '@api/instant_survey';

import { Checkbox, FolderIcon, OverlayTriggerTooltip } from '@utilities';

import { getQuestionCost, hasQuestionImage } from '../../../../../../utilities/helpers';

import styles from './_index.module.scss';

const Results = ({
  allowedQuestionTypes = [],
  areImageQuestionsDisabled = false,
  canSelectScreeners = true,
  getQuestionCostForValidation = getQuestionCost,
  imageQuestionsDisabledTooltip = '',
  maxSelectableQuestions,
  onSelectQuestion,
  questions,
  questionsToAdd,
  searchQuery,
  selectedQuestions = [],
}) => {
  if (!questions.length) {
    return (
      <p>
        {searchQuery ? 'Nothing matches your search criteria.' : 'No questions in this folder.'}
      </p>
    );
  }
  const questionsToAddCount = questionsToAdd.reduce(
    (count, question) => count + getQuestionCostForValidation(question),
    0
  );
  const hasAllowedQuestionTypes = allowedQuestionTypes.length > 0;
  const isAllowedQuestionType = (question) =>
    !hasAllowedQuestionTypes || allowedQuestionTypes.includes(question.type);
  const isDisallowedImageQuestion = (question) =>
    areImageQuestionsDisabled && hasQuestionImage(question);

  return (
    <ul className={styles['results-container']}>
      {questions.map((question) => (
        <li key={question.id}>
          {question.isFolder && (
            <button className={styles['results-folder']} onClick={() => onSelectQuestion(question)}>
              <FolderIcon /> {question.text}
            </button>
          )}
          {!question.isFolder && (
            <div className={styles['results-question']}>
              <div>
                <div className={styles['results-checkbox-wrapper']}>
                  <OverlayTriggerTooltip
                    content={
                      isDisallowedImageQuestion(question)
                        ? imageQuestionsDisabledTooltip
                        : undefined
                    }
                  >
                    <Checkbox
                      isChecked={
                        questionsToAdd.some(({ id }) => id === question.id) ||
                        selectedQuestions.some(({ id }) => id === question.id)
                      }
                      isDisabled={
                        (!questionsToAdd.some(({ id }) => id === question.id) &&
                          questionsToAddCount + getQuestionCostForValidation(question) >
                            maxSelectableQuestions) ||
                        selectedQuestions.some(({ id }) => id === question.id) ||
                        !isAllowedQuestionType(question) ||
                        isDisallowedImageQuestion(question) ||
                        (question.isScreener && !canSelectScreeners)
                      }
                      label={question.text}
                      onChange={() => onSelectQuestion(question)}
                      value={question.id}
                    />
                  </OverlayTriggerTooltip>
                </div>
                <span
                  className={classNames(styles['results-caption'], {
                    [styles['is-disabled']]:
                      selectedQuestions.some(({ id }) => id === question.id) ||
                      !isAllowedQuestionType(question) ||
                      isDisallowedImageQuestion(question) ||
                      (question.isScreener && !canSelectScreeners),
                  })}
                >
                  {question.type === QUESTION_TYPES.ESSAY.value
                    ? 'Open-ended response'
                    : question.caption}
                </span>
              </div>
              {question.image?.cdnUrl && (
                <img
                  alt="Uploaded File"
                  className={styles['results-image']}
                  src={question.image.cdnUrl}
                />
              )}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
};

Results.propTypes = {
  allowedQuestionTypes: PropTypes.array,
  areImageQuestionsDisabled: PropTypes.bool,
  canSelectScreeners: PropTypes.bool,
  getQuestionCostForValidation: PropTypes.func,
  imageQuestionsDisabledTooltip: PropTypes.string,
  maxSelectableQuestions: PropTypes.number.isRequired,
  onSelectQuestion: PropTypes.func.isRequired,
  questions: PropTypes.array.isRequired,
  questionsToAdd: PropTypes.array.isRequired,
  searchQuery: PropTypes.string,
  selectedQuestions: PropTypes.array,
};

export default Results;
